<template>
  <div class="app-shell" :class="{ 'app-shell--rail-collapsed': railCollapsed }">
    <LeftRail
      :collapsed="railCollapsed"
      @toggle="toggleRail"
    />
    <div class="app-shell__main">
      <TopBar
        :section-title="currentSectionTitle"
        @toggle-notifications="toggleNotifications"
        @toggle-help="toggleHelp"
      />
      <main class="app-shell__content">
        <LoadingOverlay scoped />
        <ErrorBoundary>
          <router-view />
        </ErrorBoundary>
      </main>
    </div>

    <NotificationsPanel
      :open="notificationsStore.panelOpen"
      @close="notificationsStore.closePanel"
    />
    <NotificationsConfigModal
      :open="notificationsStore.configModalOpen"
      @close="notificationsStore.closeConfigModal"
    />
    <HelpPanel
      :open="helpOpen"
      @close="closeHelp"
    />
    <CommandPalette
      :open="paletteOpen"
      @close="paletteOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import LeftRail from './LeftRail.vue'
import TopBar from './TopBar.vue'
import NotificationsPanel from './NotificationsPanel.vue'
import NotificationsConfigModal from './NotificationsConfigModal.vue'
import HelpPanel from './HelpPanel.vue'
import CommandPalette from './CommandPalette.vue'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import { useNotificationsStore } from '@/store/notifications.store'

const STORAGE_KEY = 'ir-shell:rail-collapsed'

export default defineComponent({
  name: 'AppShell',
  components: {
    LeftRail,
    TopBar,
    NotificationsPanel,
    NotificationsConfigModal,
    HelpPanel,
    CommandPalette,
    ErrorBoundary,
    LoadingOverlay,
  },
  setup() {
    const route = useRoute()
    const notificationsStore = useNotificationsStore()

    const getPersistedCollapsed = (): boolean => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return stored === 'true'
      } catch {
        return false
      }
    }

    const NARROW_BREAKPOINT = 768
    const isNarrow = ref<boolean>(typeof window !== 'undefined' && window.innerWidth < NARROW_BREAKPOINT)
    // Track user preference separately from forced-narrow state, so when the user
    // resizes back to wide we can restore their last choice rather than always-expand.
    const userCollapsed = ref<boolean>(getPersistedCollapsed())
    const railCollapsed = computed<boolean>(() => isNarrow.value || userCollapsed.value)
    const helpOpen = ref<boolean>(false)
    const paletteOpen = ref<boolean>(false)

    const toggleRail = () => {
      // On narrow viewports, the toggle is a no-op (rail is forced collapsed).
      if (isNarrow.value) return
      userCollapsed.value = !userCollapsed.value
    }

    watch(userCollapsed, (val) => {
      try {
        localStorage.setItem(STORAGE_KEY, String(val))
      } catch {
        // Storage unavailable — non-fatal
      }
    })

    const onResize = () => {
      isNarrow.value = window.innerWidth < NARROW_BREAKPOINT
    }

    function toggleNotifications() {
      // Mutual exclusion — opening one closes the other
      if (helpOpen.value) helpOpen.value = false
      notificationsStore.togglePanel()
    }

    function toggleHelp() {
      if (notificationsStore.panelOpen) notificationsStore.closePanel()
      helpOpen.value = !helpOpen.value
    }

    function closeHelp() {
      helpOpen.value = false
    }

    // Global keyboard shortcuts
    const onKey = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K — open the command palette (works inside inputs too — power-user override)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        paletteOpen.value = !paletteOpen.value
        // close other side panels when opening palette
        if (paletteOpen.value) {
          if (helpOpen.value) helpOpen.value = false
          if (notificationsStore.panelOpen) notificationsStore.closePanel()
        }
        return
      }
      // ? — open help panel (only when not in a text input)
      if (
        e.key === '?' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement) &&
        !(e.target as HTMLElement | null)?.isContentEditable
      ) {
        e.preventDefault()
        toggleHelp()
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', onKey)
      window.addEventListener('resize', onResize)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    })

    const currentSectionTitle = computed<string>(() => {
      return (route.meta.sectionTitle as string | undefined) || ''
    })

    return {
      railCollapsed,
      toggleRail,
      currentSectionTitle,
      notificationsStore,
      helpOpen,
      paletteOpen,
      toggleNotifications,
      toggleHelp,
      closeHelp,
    }
  },
})
</script>

<style scoped>
.app-shell {
  display: grid;
  grid-template-columns: auto 1fr;
  height: 100vh;
  width: 100%;
  background: #181b2f;
  overflow: hidden;
  font-family: var(--font-sans);
}

.app-shell__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.app-shell__content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #181b2f;
  box-sizing: border-box;
  position: relative; /* anchor for the scoped LoadingOverlay */
}

/* Mobile responsive — content padding tightens; rail force-collapses via JS. */
@media (max-width: 768px) {
  .app-shell__content {
    padding: 16px 12px;
  }
}
</style>
